// Turns the cells under a dragging finger into the path of a belt (PRD section 19.7: "arrastrar sobre la
// cuadrícula para trazar"). A fast finger skips cells and cuts corners; the path never does: it fills the
// gaps with straight steps, walks back when the finger goes back, and drops a loop when the finger
// crosses its own trail. Plain data, no DOM: the build session feeds it cells.
import { inBounds, keyOf, type Cell } from '../sim/geometry';

const same = (a: Cell, b: Cell): boolean => a[0] === b[0] && a[1] === b[1];

/** One straight step from `from` towards `to`, along the axis with the longer way to go. */
function stepTowards(from: Cell, to: Cell): Cell {
  const dc = to[0] - from[0];
  const dr = to[1] - from[1];
  if (Math.abs(dc) >= Math.abs(dr)) return [from[0] + Math.sign(dc), from[1]];
  return [from[0], from[1] + Math.sign(dr)];
}

/** The path after adding one adjacent cell: walking back pops, crossing the trail cuts the loop. */
function addAdjacent(path: Cell[], cell: Cell): Cell[] {
  const before = path[path.length - 2];
  if (before && same(before, cell)) return path.slice(0, -1);
  const seen = path.findIndex((c) => same(c, cell));
  if (seen >= 0) return path.slice(0, seen + 1);
  return [...path, cell];
}

/**
 * The path once the finger is over `cell`. Cells outside the interior of `size` are ignored, so a
 * finger that slips off the board keeps the belt it has drawn so far.
 */
export function extendPath(path: readonly Cell[], cell: Cell, size: Cell): Cell[] {
  if (!inBounds(cell, size)) return [...path];
  let out = [...path];
  // Bounded: each step gets one cell closer to the finger.
  for (let last = out.at(-1); last && !same(last, cell); last = out.at(-1)) out = addAdjacent(out, stepTowards(last, cell));
  return out.length > 0 ? out : [cell];
}

/** True if no cell repeats: what the simulation requires of a belt path. */
export const isSimplePath = (path: readonly Cell[]): boolean => new Set(path.map(keyOf)).size === path.length;
